import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Bonjour ! Je suis l'assistant virtuel de HELP ADN. Comment puis-je vous aider aujourd'hui ?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai-chat`, {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        setSessionId(response.data.session_id);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response 
        }]);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, une erreur s'est produite. Veuillez réessayer ou contacter Jennifer directement à contact@helpadn.fr" 
      }]);
    }
    setIsLoading(false);
  };

  return (
    <>
      {/* Chat Button */}
      <div className="fixed bottom-[68px] right-6 z-50 flex items-center gap-3">
        {!isOpen && (
          <div className="bg-[#C9A55C] text-black px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-pulse">
            Besoin d'aide ?
          </div>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-[#C9A55C] rounded-full flex items-center justify-center shadow-lg hover:bg-[#C9A55C]/90 transition-all"
          data-testid="aibot-toggle"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-black" />
          ) : (
            <MessageCircle className="w-6 h-6 text-black" />
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-36 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-black border border-[#C9A55C]/30 shadow-2xl rounded-lg overflow-hidden" data-testid="aibot-window">
          {/* Header */}
          <div className="bg-[#C9A55C] p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                <Bot className="w-7 h-7 text-[#C9A55C]" />
              </div>
              <div>
                <h3 className="font-black text-black text-lg">HELP ADN – Assistant IA</h3>
                <p className="text-sm text-black font-bold">Réponse dans les 2h max</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[350px] overflow-y-auto p-4 space-y-4 bg-black">
            {messages.map((msg, index) => {
              // Convert markdown links and URLs to clickable links
              const renderContent = (text) => {
                // First handle markdown links [text](url)
                let processed = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-[#C9A55C] hover:text-[#C9A55C]/80">$1</a>');
                // Then handle plain URLs that are NOT already in href attributes
                processed = processed.replace(/(?<!href=["'])(https?:\/\/[^\s<>"]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline text-[#C9A55C] hover:text-[#C9A55C]/80">$1</a>');
                return processed;
              };
              
              return (
                <div
                  key={index}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-[#C9A55C]' : 'bg-[#C9A55C]/20'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-black" />
                    ) : (
                      <Bot className="w-4 h-4 text-[#C9A55C]" />
                    )}
                  </div>
                  <div className={`max-w-[80%] p-3 rounded-lg overflow-hidden ${
                    msg.role === 'user' 
                      ? 'bg-[#C9A55C] text-black' 
                      : 'bg-[#1a1a1a] text-white border-2 border-[#C9A55C]/50'
                  }`}>
                    <p 
                      className={`text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere ${msg.role === 'user' ? 'font-black' : ''}`}
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      dangerouslySetInnerHTML={{ __html: renderContent(msg.content) }}
                    />
                  </div>
                </div>
              );
            })}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#C9A55C]/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#C9A55C]" />
                </div>
                <div className="bg-white/10 p-3 rounded-lg border border-[#C9A55C]/20">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#C9A55C] rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                    <span className="w-2 h-2 bg-[#C9A55C] rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                    <span className="w-2 h-2 bg-[#C9A55C] rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t-2 border-[#C9A55C] bg-black">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 bg-black border-2 border-[#C9A55C] rounded-lg px-4 py-3 text-white text-sm placeholder-white/50 focus:border-[#C9A55C] outline-none"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="w-10 h-10 bg-[#C9A55C] rounded-lg flex items-center justify-center hover:bg-[#C9A55C]/90 disabled:opacity-50 transition-all"
                data-testid="aibot-send-btn"
              >
                <Send className="w-4 h-4 text-black" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AIBot;
